import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, LogOut, TrendingUp, PieChart, Activity,
  CalendarDays, ArrowUpRight, Video, Phone, Monitor,
  Building2, AlertCircle, RefreshCw
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Legend
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { analytics } from "@/services/analytics";

const MEETING_MODE_ICONS = {
  google_meet: Video, zoom: Video, teams: Monitor,
  phone: Phone, office: Building2,
};
const MEETING_MODE_LABELS = {
  google_meet: "Google Meet", zoom: "Zoom", teams: "MS Teams",
  phone: "Phone Call", office: "Office Visit",
};

const PERIODS = ["1M", "3M", "6M", "1Y", "3Y", "5Y"];
const ALLOCATION_COLORS = ["#101828", "#4F63F0", "#1CB0A4", "#8B5CF6", "#F59E0B", "#6B7280"];

export default function Portfolio() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [chart, setChart] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [activity, setActivity] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [period, setPeriod] = useState("1Y");
  const [loadingAll, setLoadingAll] = useState(true);
  const [loadingChart, setLoadingChart] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  const loadAll = useCallback(async () => {
    if (!user) return;
    setLoadingAll(true);
    setError(null);
    try {
      const [summaryRes, chartRes, allocRes, activityRes, meetingsRes] = await Promise.all([
        api.get("/portfolio/summary"),
        api.get(`/portfolio/chart?period=${period}`),
        api.get("/portfolio/allocation"),
        api.get("/portfolio/activity"),
        api.get("/portfolio/meetings"),
      ]);
      setSummary(summaryRes.data);
      setChart(chartRes.data);
      setAllocation(allocRes.data);
      setActivity(activityRes.data);
      setMeetings(meetingsRes.data);
      analytics.portfolioView();
    } catch (e) {
      setError(errMsg(e));
      toast.error(errMsg(e));
    } finally {
      setLoadingAll(false);
    }
  }, [user, period]);

  const loadChart = useCallback(async () => {
    if (!user) return;
    setLoadingChart(true);
    try {
      const { data } = await api.get(`/portfolio/chart?period=${period}`);
      setChart(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoadingChart(false);
    }
  }, [user, period]);

  useEffect(() => { if (user) loadAll(); }, [user]);
  useEffect(() => { if (user && !loadingAll) loadChart(); }, [period]);

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center bg-[hsl(var(--zt-beige))]"><Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} /></div>;
  }

  const isPlaceholder = summary?.data_source === "placeholder";

  return (
    <div className="min-h-screen bg-[hsl(var(--zt-beige))] pb-24">
      {/* Header */}
      <div className="bg-[hsl(var(--zt-teal))] text-white relative overflow-hidden">
        <div aria-hidden className="absolute -top-40 -right-40 w-[450px] h-[450px] rounded-full opacity-[0.15] blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, #4F63F0 0%, transparent 60%)" }} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-24 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="transition-transform hover:scale-[1.02]">
              <img src="/zenstrust-logo.png" alt="Zenstrust" className="h-14 w-auto rounded-md object-contain" />
            </button>
            <span className="hidden sm:inline font-heading uppercase tracking-[0.25em] text-[10px] text-[hsl(var(--zt-gold))] font-bold border-l border-white/15 pl-4">
              Portfolio
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/dashboard")}
              className="text-white hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-4 rounded-full border border-white/10 hidden sm:flex">
              Dashboard
            </Button>
            <Button variant="ghost" onClick={() => loadAll()}
              className="text-white hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-4 rounded-full border border-white/10">
              <RefreshCw size={13} className="mr-1.5" /> Refresh
            </Button>
            <Button variant="ghost" onClick={async () => { await logout(); navigate("/"); }}
              className="text-white hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-5 rounded-full border border-white/10">
              <LogOut size={15} className="mr-2" /> Log out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12 space-y-8">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl text-[hsl(var(--zt-teal))] font-bold">
            Portfolio Dashboard
          </h1>
          <p className="text-sm text-[hsl(var(--zt-charcoal))]/60 mt-1 font-body">
            Your NRI investment overview and performance metrics.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4 flex items-center gap-3">
            <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={loadAll} className="ml-auto text-xs font-heading uppercase tracking-wider text-red-600 hover:text-red-800">
              Retry
            </button>
          </div>
        )}

        {loadingAll ? (
          <PortfolioSkeleton />
        ) : (
          <div className="space-y-8">
            {/* Onboarding state — placeholder data */}
            {isPlaceholder && (
              <div className="bg-[hsl(var(--zt-teal))]/5 border border-[hsl(var(--zt-teal))]/15 rounded-2xl px-6 py-5 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[hsl(var(--zt-gold))]/15 grid place-items-center flex-shrink-0">
                  <TrendingUp size={18} className="text-[hsl(var(--zt-gold))]" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-[hsl(var(--zt-teal))] text-sm">Portfolio activation pending</p>
                  <p className="text-sm text-[hsl(var(--zt-charcoal))]/65 mt-0.5 font-body">
                    {summary?.message}
                  </p>
                  <div className="flex gap-3 mt-3">
                    <button onClick={() => navigate("/documents")}
                      className="inline-flex items-center gap-1.5 bg-[hsl(var(--zt-gold))] text-white rounded-full px-4 h-8 font-heading uppercase tracking-wider text-[10px] font-bold hover:bg-[hsl(var(--zt-gold))]/90 transition-colors">
                      Complete KYC <ArrowUpRight size={11} />
                    </button>
                    <button onClick={() => navigate("/dashboard")}
                      className="inline-flex items-center gap-1.5 border border-[hsl(var(--zt-teal))]/20 text-[hsl(var(--zt-teal))]/70 rounded-full px-4 h-8 font-heading uppercase tracking-wider text-[10px] hover:border-[hsl(var(--zt-teal))]/40 transition-colors">
                      Request Advisory Call
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Metrics row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                label="Portfolio Value"
                value={summary?.metrics?.total_portfolio_value}
                prefix="₹"
                empty={isPlaceholder}
              />
              <MetricCard
                label="Total Invested"
                value={summary?.metrics?.total_invested}
                prefix="₹"
                empty={isPlaceholder}
              />
              <MetricCard
                label="Current Returns"
                value={summary?.metrics?.current_returns}
                prefix="₹"
                signed
                empty={isPlaceholder}
              />
              <MetricCard
                label="CAGR"
                value={summary?.metrics?.cagr}
                suffix="%"
                empty={isPlaceholder}
              />
            </div>

            {/* Charts row */}
            <div className="grid lg:grid-cols-12 gap-6">
              {/* Portfolio growth chart */}
              <div className="lg:col-span-8 bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-[hsl(var(--zt-teal))]/8 grid place-items-center">
                      <TrendingUp size={15} className="text-[hsl(var(--zt-teal))]" />
                    </div>
                    <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">Portfolio Growth</h3>
                  </div>
                  <div className="flex items-center gap-1">
                    {PERIODS.map(p => (
                      <button
                        key={p}
                        onClick={() => setPeriod(p)}
                        className={`h-7 px-2.5 rounded-lg font-heading uppercase tracking-wider text-[10px] font-semibold transition-colors ${
                          period === p
                            ? "bg-[hsl(var(--zt-teal))] text-white"
                            : "text-[hsl(var(--zt-charcoal))]/50 hover:bg-[hsl(var(--zt-beige-deep))]"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {isPlaceholder ? (
                  <ChartPlaceholder height={220} message="Chart data will appear when your portfolio is active" />
                ) : loadingChart ? (
                  <div className="h-[220px] grid place-items-center">
                    <Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={22} />
                  </div>
                ) : chart?.portfolio_value?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={chart.labels.map((l, i) => ({
                      label: l,
                      portfolio: chart.portfolio_value[i],
                      invested: chart.invested_value[i],
                    }))}>
                      <defs>
                        <linearGradient id="pfGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#101828" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#101828" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0ece7" />
                      <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "Outfit" }} />
                      <YAxis tick={{ fontSize: 10, fontFamily: "Outfit" }} tickFormatter={v => `₹${v}`} />
                      <Tooltip contentStyle={{ fontSize: 12, fontFamily: "Outfit", borderRadius: 8 }} />
                      <Area type="monotone" dataKey="portfolio" stroke="#101828" strokeWidth={2} fill="url(#pfGrad)" name="Portfolio" />
                      <Area type="monotone" dataKey="invested" stroke="#4F63F0" strokeWidth={1.5} fill="none" strokeDasharray="4 3" name="Invested" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <ChartPlaceholder height={220} message="No chart data available for this period" />
                )}
              </div>

              {/* Allocation chart */}
              <div className="lg:col-span-4 bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm">
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-8 h-8 rounded-xl bg-[hsl(var(--zt-gold))]/10 grid place-items-center">
                    <PieChart size={15} className="text-[hsl(var(--zt-gold))]" />
                  </div>
                  <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">Asset Allocation</h3>
                </div>

                {isPlaceholder || !allocation?.segments?.length ? (
                  <AllocationPlaceholder />
                ) : (
                  <div>
                    <ResponsiveContainer width="100%" height={160}>
                      <RechartsPie>
                        <Pie
                          data={allocation.segments}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={70}
                          dataKey="value"
                          nameKey="label"
                        >
                          {allocation.segments.map((_, i) => (
                            <Cell key={i} fill={ALLOCATION_COLORS[i % ALLOCATION_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ fontSize: 12, fontFamily: "Outfit", borderRadius: 8 }}
                          formatter={v => [`${v}%`, ""]} />
                      </RechartsPie>
                    </ResponsiveContainer>
                    <div className="space-y-1.5 mt-2">
                      {allocation.segments.map((s, i) => (
                        <div key={s.label} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: ALLOCATION_COLORS[i % ALLOCATION_COLORS.length] }} />
                            <span className="text-[hsl(var(--zt-charcoal))]/70">{s.label}</span>
                          </div>
                          <span className="font-semibold text-[hsl(var(--zt-teal))]">{s.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Activity + Meetings */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent activity */}
              <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40 flex items-center gap-2.5">
                  <Activity size={13} className="text-[hsl(var(--zt-teal))]" />
                  <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">Recent Activity</h3>
                </div>
                {activity.length === 0 ? (
                  <div className="px-6 py-10 text-center">
                    <Activity size={24} className="mx-auto mb-3 text-[hsl(var(--zt-charcoal))]/20" />
                    <p className="text-sm text-[hsl(var(--zt-charcoal))]/45">
                      Portfolio activity will appear here once your investments are active.
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y divide-[hsl(var(--zt-beige-deep))]/60">
                    {activity.map((a, i) => (
                      <li key={i} className="px-6 py-3.5 flex items-center gap-3">
                        <div className="w-7 h-7 rounded-xl bg-[hsl(var(--zt-beige))] grid place-items-center flex-shrink-0">
                          <Activity size={12} className="text-[hsl(var(--zt-teal))]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[hsl(var(--zt-teal))] truncate">{a.description}</p>
                          <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/50">{a.date}</p>
                        </div>
                        {a.amount && <span className={`text-sm font-bold ${a.type === "credit" ? "text-emerald-600" : "text-red-500"}`}>
                          {a.type === "credit" ? "+" : "-"}₹{a.amount}
                        </span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Upcoming meetings */}
              <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <CalendarDays size={13} className="text-[hsl(var(--zt-teal))]" />
                    <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">Upcoming Meetings</h3>
                  </div>
                  <button onClick={() => navigate("/appointments")}
                    className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-gold))] hover:text-[hsl(var(--zt-gold))]/80 flex items-center gap-1">
                    View all <ArrowUpRight size={11} />
                  </button>
                </div>
                {meetings.length === 0 ? (
                  <div className="px-6 py-10 text-center">
                    <CalendarDays size={24} className="mx-auto mb-3 text-[hsl(var(--zt-charcoal))]/20" />
                    <p className="text-sm text-[hsl(var(--zt-charcoal))]/45">No upcoming meetings scheduled.</p>
                    <button onClick={() => navigate("/#lead-form")}
                      className="mt-3 text-xs font-heading uppercase tracking-wider text-[hsl(var(--zt-gold))] hover:underline">
                      Schedule an advisory call
                    </button>
                  </div>
                ) : (
                  <ul className="divide-y divide-[hsl(var(--zt-beige-deep))]/60">
                    {meetings.map(m => {
                      const ModeIcon = MEETING_MODE_ICONS[m.meeting_mode] || Video;
                      return (
                        <li key={m.appointment_id} className="px-6 py-4 flex items-start gap-3">
                          <div className="bg-[hsl(var(--zt-teal))]/5 border border-[hsl(var(--zt-teal))]/10 rounded-xl px-3 py-2 text-center flex-shrink-0 min-w-[52px]">
                            <p className="font-heading text-[9px] uppercase tracking-wider text-[hsl(var(--zt-teal))]/60 font-bold">{fmtMonth(m.scheduled_at)}</p>
                            <p className="font-display text-xl text-[hsl(var(--zt-teal))] font-bold leading-none">{fmtDay(m.scheduled_at)}</p>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[hsl(var(--zt-teal))]">{m.advisor_name}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <ModeIcon size={11} className="text-[hsl(var(--zt-charcoal))]/40" />
                              <span className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{MEETING_MODE_LABELS[m.meeting_mode]} · {fmtTime(m.scheduled_at)}</span>
                            </div>
                          </div>
                          {m.meeting_link && (
                            <a href={m.meeting_link} target="_blank" rel="noopener noreferrer"
                              className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-gold))] hover:underline flex-shrink-0 mt-1">
                              Join
                            </a>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MetricCard({ label, value, prefix = "", suffix = "", signed = false, empty }) {
  return (
    <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-5 shadow-sm">
      <p className="font-heading uppercase tracking-[0.18em] text-[10px] text-[hsl(var(--zt-charcoal))]/50 font-bold mb-2">{label}</p>
      {empty || value == null ? (
        <div className="space-y-1.5">
          <div className="h-7 w-24 bg-[hsl(var(--zt-beige-deep))]/60 rounded-lg animate-pulse" />
          <p className="text-[10px] text-[hsl(var(--zt-charcoal))]/35 font-heading uppercase tracking-wider">Pending activation</p>
        </div>
      ) : (
        <p className={`font-display text-2xl font-bold ${signed && value > 0 ? "text-emerald-600" : signed && value < 0 ? "text-red-500" : "text-[hsl(var(--zt-teal))]"}`}>
          {signed && value > 0 ? "+" : ""}{prefix}{typeof value === "number" ? value.toLocaleString("en-IN") : value}{suffix}
        </p>
      )}
    </div>
  );
}

function ChartPlaceholder({ height = 200, message }) {
  return (
    <div className={`grid place-items-center text-center`} style={{ height }}>
      <div>
        <TrendingUp size={28} className="mx-auto mb-3 text-[hsl(var(--zt-charcoal))]/20" />
        <p className="text-sm text-[hsl(var(--zt-charcoal))]/45 max-w-[220px] mx-auto">{message}</p>
      </div>
    </div>
  );
}

function AllocationPlaceholder() {
  const cats = ["Equity", "Debt", "Real Estate", "Gold", "Cash"];
  return (
    <div className="space-y-3 py-2">
      {cats.map((c, i) => (
        <div key={c} className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-sm bg-[hsl(var(--zt-beige-deep))]" />
          <div className="flex-1 h-1.5 bg-[hsl(var(--zt-beige-deep))]/60 rounded-full overflow-hidden">
            <div className="h-full bg-[hsl(var(--zt-beige-deep))] rounded-full w-0" />
          </div>
          <span className="text-[10px] text-[hsl(var(--zt-charcoal))]/35 w-6">—</span>
        </div>
      ))}
      <p className="text-[10px] text-[hsl(var(--zt-charcoal))]/40 text-center pt-2">
        Allocation will appear when portfolio is active
      </p>
    </div>
  );
}

function PortfolioSkeleton() {
  return (
    <div className="space-y-8 animate-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="bg-white rounded-2xl h-24 border border-[hsl(var(--zt-beige-deep))]" />)}
      </div>
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 bg-white rounded-2xl h-72 border border-[hsl(var(--zt-beige-deep))]" />
        <div className="lg:col-span-4 bg-white rounded-2xl h-72 border border-[hsl(var(--zt-beige-deep))]" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl h-64 border border-[hsl(var(--zt-beige-deep))]" />
        <div className="bg-white rounded-2xl h-64 border border-[hsl(var(--zt-beige-deep))]" />
      </div>
    </div>
  );
}

function fmtMonth(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString(undefined, { month: "short" });
}
function fmtDay(d) {
  if (!d) return "";
  return String(new Date(d).getDate());
}
function fmtTime(d) {
  if (!d) return "";
  return new Date(d).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
